<template>
    <Overlay :show="showLoading">
        <b-row>
            <b-col>
                <h2>
                    {{ id ? 'Edit ' + role.title : 'Create new user role' }}
                </h2>
            </b-col>
            <b-col class="pb-2">
                <CreateUpdateServiceActions
                    class="float-right"
                    @save="save"
                    @close="close"
                />
            </b-col>
        </b-row>

        <validation-observer ref="userRole">
            <b-row>
                <b-col
                    md="6"
                    lg="6"
                >
                    <b-card title="General info">
                        <b-form-group
                            :label="$t('Component(Label):name')"
                            label-for="title"
                        >
                            <validation-provider
                                #default="{ errors }"
                                :name="$t('Component(Label):name')"
                                rules="required|max:64"
                            >
                                <b-form-input
                                    id="title"
                                    v-model="role.title"
                                    :state="errors.length > 0 ? false:null"
                                    :placeholder="$t('Component(Placeholder):name')"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>

                        <b-form-group
                            :label="$t('Component(Label):description')"
                            label-for="description"
                        >
                            <validation-provider
                                #default="{ errors }"
                                :name="$t('Component(Label):description')"
                                rules="max:255"
                            >
                                <b-form-textarea
                                    id="description"
                                    v-model="role.description"
                                    :state="errors.length > 0 ? false:null"
                                    rows="3"
                                    max-rows="6"
                                    :placeholder="$t('Component(Placeholder):description')"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-card>

                    <b-card title="Services">
                        <b-form-group
                            :label="$t('Component(Grid):column-services')"
                            label-for="services"
                        >
                            <treeselect
                                id="services"
                                v-model="role.services"
                                multiple
                                no-children-text=""
                                :normalizer="normalizeService"
                                :placeholder="$t('Component(Placeholder):services')"
                                :options="services"
                            />
                        </b-form-group>
                    </b-card>
                </b-col>

                <b-col
                    md="6"
                    lg="6"
                >
                    <b-card title="Capabilities">
                        <b-form-group
                            :label="$t('Component(Label):capabilities')"
                            label-for="capabilities"
                        >
                            <div
                                v-if="capabilities.length === 0"
                                class="text-muted"
                            >
                                No capabilities available
                            </div>
                            <div
                                v-for="capability in capabilities"
                                :key="capability.key"
                                class="capability-option"
                            >
                                <b-form-checkbox
                                    v-model="role.capabilities"
                                    :value="capability.key"
                                >
                                    {{ capability.title }}
                                </b-form-checkbox>
                                <div class="capability-description ml-2">
                                    {{ capability.description }}
                                </div>
                            </div>
                        </b-form-group>
                    </b-card>
                </b-col>
            </b-row>
        </validation-observer>

        <b-row>
            <b-col>
                <CreateUpdateServiceActions
                    class="float-right"
                    @save="save"
                    @close="close"
                />
            </b-col>
        </b-row>
    </Overlay>
</template>

<script>
import Overlay from '@core/components/common/Overlay.vue'
import {
    BRow, BCol, BCard, BFormTextarea, BFormGroup, BFormInput, BFormCheckbox,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import Treeselect from '@riophae/vue-treeselect'
import { SystemApi, UserApi } from '@core/api'
import backendResponse from '@core/mixins/ui/backend-response'
import { max, required } from '@validations'
import CreateUpdateServiceActions from '@/views/system/Security/components/CreateUpdateServiceActions.vue'

export default {
    components: {
        Overlay,
        BRow,
        BCol,
        BCard,
        BFormTextarea,
        BFormGroup,
        BFormInput,
        BFormCheckbox,
        ValidationProvider,
        ValidationObserver,
        Treeselect,
        CreateUpdateServiceActions,
    },
    mixins: [backendResponse],
    props: {
        id: {
            type: [String, Number],
            default: null,
        },
    },
    data() {
        return {
            showLoading: true,
            role: {
                title: '',
                description: '',
                services: [],
                capabilities: [],
            },
            services: [],
            capabilities: [],
            required,
            max,
        }
    },
    created() {
        Promise
            .all([
                this.loadServices(),
                this.loadCapabilities(),
                this.loadRole(),
            ])
            .finally(() => {
                this.showLoading = false
            })
    },
    methods: {
        gatherSaveAttributes() {
            return {
                title: this.role.title,
                description: this.role.description,
                services: this.role.services,
                capabilities: this.role.capabilities,
            }
        },
        save() {
            this.$refs.userRole.validate().then(isValid => {
                if (!isValid) {
                    return
                }

                this.showLoading = true

                const request = this.id
                    ? UserApi.updateSystemRole(this.id, this.gatherSaveAttributes())
                    : UserApi.createSystemRole(this.gatherSaveAttributes())

                request
                    .then(() => {
                        this.showDefaultSuccessMessage('User role has been successfully saved')
                        this.close()
                    })
                    .catch(this.handleBackendError)
                    .finally(() => {
                        this.showLoading = false
                    })
            })
        },
        close() {
            this.$router.go(-1)
        },
        normalizeService(service) {
            return {
                id: service.id,
                label: service.title,
                children: service.children,
            }
        },
        loadServices() {
            return SystemApi.services
                .getHierarchy()
                .then(r => {
                    this.services = r.data
                })
                .catch(this.handleBackendError)
        },
        loadCapabilities() {
            return UserApi
                .getSystemRoleCapabilities()
                .then(r => {
                    this.capabilities = r.data
                })
                .catch(this.handleBackendError)
        },
        loadRole() {
            if (!this.id) {
                return Promise.resolve()
            }

            return UserApi
                .getSystemRole(this.id)
                .then(r => {
                    this.role = r.data
                })
                .catch(this.handleBackendError)
        },
    },
}
</script>

<style scoped>
@import '../../../../node_modules/@riophae/vue-treeselect/dist/vue-treeselect.css';

.capability-option {
    padding: .5rem 0;
    border-bottom: 1px solid #ebe9f1;
}

.capability-option:last-child {
    border-bottom: 0;
}

.capability-description {
    color: #6e6b7b;
    font-size: .95rem;
    line-height: 1.35;
    margin-top: .2rem;
}
</style>
